/**
 * How a pull under way reads on the operator console.
 *
 * The pull is the engine's. Until 28 Sep 2026 the console showed only this
 * app's own work — the copy afterwards, which is seconds — so an operator who
 * pressed Pull watched a page that did not move for the minutes the engine
 * spent on the county. The engine now says where each sweep is, and this turns
 * that into one bar and one line.
 *
 * The counts are facts and the time left is an estimate from each county's
 * last pull; the wording keeps the two apart. "About 12 min left" is a guess
 * and says so, and a county that runs past last time's length is named as
 * running long rather than the bar pretending it is nearly done.
 */

import type { AdminOverview, EngineRun } from "../api/types";

/** "under a minute", "12 min", "1.7 h". */
export function duration(seconds: number): string {
  if (seconds < 60) return "under a minute";
  if (seconds < 90 * 60) return `${Math.round(seconds / 60)} min`;
  return `${(seconds / 3600).toFixed(1).replace(/\.0$/, "")} h`;
}

/** The estimate, worded as one. */
export function timeLeft(seconds: number | null): string {
  if (seconds === null) return "time left unknown";
  if (seconds <= 0) return "nearly done";
  return `about ${duration(seconds)} left`;
}

export interface PullLine {
  title: string;
  line: string;
  /** 0–100, for the bar's width. */
  percent: number;
  /** "1 failed so far: Lee", or "". */
  failed: string;
}

export function describePull(run: EngineRun): PullLine {
  const title =
    run.kind === "scheduled" ? "Morning sweep" : "Pull you asked for";
  const parts: string[] = [];
  const current = run.current;

  if (current) {
    parts.push(`County ${Math.min(run.done + 1, run.counties)} of ${run.counties}`);
    parts.push(current.name);
    if (current.detail) parts.push(current.detail);
    if (run.overrun && current.elapsed_seconds !== null) {
      parts.push(
        `taking longer than last time (${duration(current.elapsed_seconds)} so far)`,
      );
      if (run.remaining_seconds) {
        parts.push(`at least ${duration(run.remaining_seconds)} more after it`);
      }
    } else {
      parts.push(timeLeft(run.remaining_seconds));
    }
  } else {
    parts.push(`${run.done} of ${run.counties} counties done`);
  }

  const failed = run.failed.length
    ? `${run.failed.length} failed so far: ${run.failed.join(", ")}`
    : "";
  const percent = Math.round(Math.min(Math.max(run.fraction, 0), 1) * 100);
  return { title, line: parts.join(" · "), percent, failed };
}

/**
 * What to say while a pull is being watched but the engine reports none —
 * the moment before it starts, or between the engine finishing and the copy.
 * Empty when there is nothing to say.
 */
export function watchNote(overview: AdminOverview): string {
  const { pulls, refresh } = overview;
  if (pulls.runs.length > 0) return "";
  if (refresh.running) return "Copying what the engine pulled into this app…";
  if (!refresh.watching) return "";
  if (!pulls.reachable) return "The engine's progress could not be read.";
  return (
    "Waiting on the engine. Each county is copied here within 20 seconds " +
    "of the engine finishing it."
  );
}

/** Whether the console should keep polling. */
export function pullBusy(overview: AdminOverview | null): boolean {
  if (!overview) return false;
  return (
    overview.refresh.running ||
    overview.refresh.watching ||
    overview.pulls.runs.length > 0
  );
}
